import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FRUITS } from "@/hooks/useGameStateV2";
import { Sparkles, Zap, X, Shuffle } from "lucide-react";

// Shuffle Alert Banner
export function ShuffleAlert({ isVisible }) {
  if (!isVisible) return null;
  
  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-bounce-in">
      <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500 px-6 py-3 rounded-full shadow-lg border-2 border-blue-300 flex items-center gap-3">
        <Shuffle className="w-6 h-6 text-white animate-spin" />
        <div className="text-center">
          <p className="font-display text-2xl font-bold text-white">SHUFFLE!</p>
          <p className="font-body text-sm text-blue-100">No moves - board reshuffled</p>
        </div>
        <Shuffle className="w-6 h-6 text-white animate-spin" />
      </div>
    </div>
  );
}

// Double Points Alert Banner
export function DoublePointsAlert({ isVisible }) {
  if (!isVisible) return null;
  
  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-bounce-in">
      <div className="bg-gradient-to-r from-warning via-yellow-400 to-warning px-6 py-3 rounded-full shadow-lg border-2 border-yellow-300 flex items-center gap-3">
        <Sparkles className="w-6 h-6 text-yellow-800 animate-pulse" />
        <div className="text-center">
          <p className="font-display text-2xl font-bold text-yellow-900">2X POINTS</p>
          <p className="font-body text-sm text-yellow-800">Active for 1 turn!</p>
        </div>
        <Sparkles className="w-6 h-6 text-yellow-800 animate-pulse" />
      </div>
    </div>
  );
}

// Double Points Active Indicator (shown in game UI)
export function DoublePointsIndicator({ isActive }) {
  if (!isActive) return null;
  
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-warning/20 border-2 border-warning rounded-full animate-pulse">
      <Zap className="w-4 h-4 text-warning" />
      <span className="font-display font-bold text-warning text-sm">2X POINTS</span>
    </div>
  );
}

// Double XP Active Indicator (shown in game UI when 2XP consumable is active)
export function DoubleXPIndicator({ isActive, turnsLeft }) {
  if (!isActive) return null;
  
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-2 border-purple-400 rounded-full animate-pulse">
      <Sparkles className="w-4 h-4 text-purple-500" />
      <span className="font-display font-bold text-purple-600 text-sm">2X XP</span>
      <span className="font-mono text-xs text-purple-500 bg-purple-100 px-1.5 py-0.5 rounded">
        {turnsLeft} turns
      </span>
    </div>
  );
}

// Insta-Elimination Fruit Picker Modal
export function InstaEliminationPicker({ isOpen, onSelectFruit, onCancel }) {
  const fruitOptions = Object.values(FRUITS);
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md bg-card border-2 border-accent">
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-3">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent/30 to-primary/30 flex items-center justify-center animate-pulse">
              <Zap className="w-8 h-8 text-accent" />
            </div>
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-accent">
            Insta-Elimination!
          </DialogTitle>
          <DialogDescription className="font-body text-muted-foreground">
            Choose a fruit to eliminate ALL of that type from the board!
          </DialogDescription>
        </DialogHeader>

        {/* Fruit Options */}
        <div className="grid grid-cols-5 gap-3 py-4">
          {fruitOptions.map((fruit) => (
            <button
              key={fruit.id}
              onClick={() => onSelectFruit(fruit.id)}
              className="flex flex-col items-center gap-1 p-3 rounded-xl bg-muted hover:bg-accent/20 border-2 border-transparent hover:border-accent transition-all duration-200 hover:scale-110"
            >
              <span className="text-4xl">{fruit.emoji}</span>
              <span className="font-body text-xs text-muted-foreground">{fruit.name}</span>
            </button>
          ))}
        </div>

        {/* Cancel button */}
        <Button
          onClick={onCancel}
          variant="outline"
          className="w-full font-display"
        >
          <X className="w-4 h-4 mr-2" />
          Save for Later
        </Button>
        
        <p className="text-center text-xs text-muted-foreground font-body">
          This ability does not cost a move!
        </p>
      </DialogContent>
    </Dialog>
  );
}

// Insta-Elimination Available Indicator (shown in game UI)
export function InstaEliminationIndicator({ isActive, onClick }) {
  if (!isActive) return null;
  
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent/20 border-2 border-accent rounded-full hover:bg-accent/30 transition-all animate-pulse cursor-pointer"
    >
      <Zap className="w-4 h-4 text-accent" />
      <span className="font-display font-bold text-accent text-sm">INSTA-ELIM</span>
      <span className="text-xs text-accent/70">Tap to use</span>
    </button>
  );
}
